/**
 *  @class
 *  @function Predictive Search
 *  
 *  Enhanced by Tom on 3/11/25
 *  - Added support for searching by SKU in the predictive search
 *  - Added special handling for SKU format searches to improve results
 *  - Implemented fallback search mechanism for SKUs that may return 422 errors
 *  - Added hiding of empty article/collection sections when searching by SKU
 */
if (!customElements.get('search-form')) {
  class SearchForm extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.form = this.querySelector('form');
      this.input = this.querySelector('input[type="search"]');
      this.predictiveSearchResults = this.querySelector('.thb-predictive-search');
      this.cc = this.querySelector('.searchform--click-capture');

      this.setupEventListeners();
    }

    setupEventListeners() {
      this.form.addEventListener('submit', this.onFormSubmit.bind(this));
      this.predictiveSearchResults.addEventListener('focusout', this.close.bind(this));

      this.input.addEventListener('input', debounce((event) => {
        this.onChange(event);
      }, 300).bind(this));

      this.cc.addEventListener('click', this.close.bind(this));

      document.addEventListener('keyup', (e) => {
        if (e.code) {
          if (e.code.toUpperCase() === 'ESCAPE') {
            this.close();
          }
        }
      });
      this.input.addEventListener('focus', this.open.bind(this));
    }

    getQuery() {
      return this.input.value.trim();
    }

    onChange() {
      const searchTerm = this.getQuery();

      if (!searchTerm.length) {
        document.body.classList.remove('open-search');
        this.predictiveSearchResults.setAttribute('inert', '');
        return;
      }
      document.body.classList.add('open-search');
      this.predictiveSearchResults.removeAttribute('inert');
      this.getSearchResults(searchTerm);
    }

    onFormSubmit(event) {
      if (!this.getQuery().length) {
        event.preventDefault();
      }
    }

    onFocus() {
      const searchTerm = this.getQuery();

      if (!searchTerm.length) {
        return;
      }

      this.getSearchResults(searchTerm);
    }

    getSearchResults(searchTerm) {
      this.predictiveSearchResults.classList.add('loading');
      
      // ENHANCEMENT (3/11/25): Check if search term looks like a SKU
      // SKU format: alphanumeric with possible hyphens/underscores, 5+ characters
      const looksLikeSku = /^[A-Za-z0-9\-_]+$/.test(searchTerm) && searchTerm.length >= 5;
      
      // Build URL with appropriate search parameters
      let searchParams = new URLSearchParams();
      searchParams.append('q', searchTerm);
      searchParams.append('section_id', 'predictive-search');
      
      // ENHANCEMENT (3/11/25): Use specialized search parameters for SKU searches
      if (looksLikeSku) {
        // For SKUs, just search products with variant.sku field
        searchParams.append('resources[type]', 'product');
        searchParams.append('resources[fields]', 'title,variants.sku');
        searchParams.append('resources[limit]', '10');
      } else {
        // Regular search for all content types
        searchParams.append('resources[type]', 'product,article,collection');
        searchParams.append('resources[limit]', '10');
      }
      
      const searchUrl = `${theme.routes.predictive_search_url}?${searchParams.toString()}`;
      
      fetch(searchUrl)
        .then(response => {
          this.predictiveSearchResults.classList.remove('loading');
          
          if (!response.ok) {
            // ENHANCEMENT (3/11/25): Special handling for 422 errors with SKU searches
            if (response.status === 422 && looksLikeSku) {
              this.fallbackSkuSearch(searchTerm);
              return null;
            }
            
            throw new Error();
          }
          
          return response.text();
        })
        .then(text => {
          if (text === null) return; // Handle fallback case

          const parser = new DOMParser();
          const htmlDocument = parser.parseFromString(text, 'text/html');
          const section = htmlDocument.querySelector('#shopify-section-predictive-search');
          
          if (!section) {
            this.predictiveSearchResults.innerHTML = '<p style="padding: 15px;">No results found. Please try a different search.</p>';
            return;
          }
          
          this.renderSearchResults(section.innerHTML);
          
          // ENHANCEMENT (3/11/25): Hide empty sections for SKU searches
          if (looksLikeSku) {
            this.hideEmptySections();
          }
        })
        .catch(() => {
          this.predictiveSearchResults.classList.remove('loading');
          this.predictiveSearchResults.innerHTML = `
            <div style="padding: 15px; text-align: center;">
              <p>Sorry, we couldn't complete your search right now.</p>
              <p style="font-size: 0.9em; color: #666;">Try a different search term or try again later.</p>
            </div>
          `;
        });
    }
    
    // ENHANCEMENT (3/11/25): Fallback search for SKUs that may return 422 errors
    // This simpler search works when the specialized SKU search parameters fail
    fallbackSkuSearch(searchTerm) {
      const searchParams = new URLSearchParams();
      searchParams.append('q', searchTerm);
      searchParams.append('section_id', 'predictive-search');
      searchParams.append('resources[type]', 'product');
      searchParams.append('resources[limit]', '10');
      
      const searchUrl = `${theme.routes.predictive_search_url}?${searchParams.toString()}`;
      
      fetch(searchUrl)
        .then(response => {
          if (!response.ok) {
            throw new Error();
          }
          return response.text();
        })
        .then(text => {
          const parser = new DOMParser();
          const htmlDocument = parser.parseFromString(text, 'text/html');
          const section = htmlDocument.querySelector('#shopify-section-predictive-search');
          
          if (!section) {
            this.predictiveSearchResults.innerHTML = '<p style="padding: 15px;">No results found for this SKU.</p>';
            return;
          }
          
          this.renderSearchResults(section.innerHTML);
          this.hideEmptySections();
        })
        .catch(() => {
          this.predictiveSearchResults.innerHTML = '<p style="padding: 15px;">No results found for this SKU.</p>';
        });
    }

    renderSearchResults(resultsMarkup) {
      this.predictiveSearchResults.innerHTML = resultsMarkup;
      
      const submitButton = this.querySelector('#search-results-submit');
      if (submitButton) {
        submitButton.addEventListener('click', () => {
          this.form.submit();
        });
      }
    }
    
    // ENHANCEMENT (3/11/25): Hide empty sections when doing SKU searches
    // This improves the UX by removing empty article and collection sections
    hideEmptySections() {
      // Hide any empty article or collection sections
      const articleSection = this.predictiveSearchResults.querySelector('.thb-predictive-search--label.z-3');
      const collectionSection = this.predictiveSearchResults.querySelector('.thb-predictive-search--label.z-4');
      
      if (articleSection) {
        const nextEl = articleSection.nextElementSibling;
        if (nextEl && nextEl.children.length === 0) {
          articleSection.style.display = 'none';
          nextEl.style.display = 'none';
        }
      }
      
      if (collectionSection) {
        const nextEl = collectionSection.nextElementSibling;
        if (nextEl && nextEl.children.length === 0) {
          collectionSection.style.display = 'none';
          nextEl.style.display = 'none';
        }
      }
    }

    close() {
      this.predictiveSearchResults.setAttribute('inert', '');
      document.body.classList.remove('open-search');
    }
    
    open() {
      document.body.classList.add('open-search');
      this.predictiveSearchResults.removeAttribute('inert');
    }
  }
  customElements.define('search-form', SearchForm);
}